/**
 * Lioness Wishlist Page
 * Полная локализация интерфейса, GraphQL Storefront API и роутов
 */

const STOREFRONT_TOKEN = 'b17534ad28e9c2f5323d49e3c55b70ba';
const STOREFRONT_URL = 'https://lioneslingerie.com/api/2024-01/graphql.json';

const i18n = window.__lionessWishlistPageI18n || {
  rootUrl: '',
  cartAddUrl: '/cart/add.js',
  addToCart: 'Add to Cart',
  soldOut: 'Sold Out',
  selectOptions: 'Select Options',
  shareTitle: 'Share Wishlist',
  shareHint: 'Copy the link below to share your wishlist:',
  copy: 'Copy',
  copied: 'Copied!',
  clearConfirm: 'Are you sure you want to clear your wishlist?'
};

// Преобразование локали в формат GraphQL LanguageCode (например, pt-PT -> PT_PT, es -> ES)
function getGraphQLLanguageCode() {
  const lang = (document.documentElement.lang || 'en').replace('-', '_').toUpperCase();
  return lang.length > 5 ? lang.substring(0, 5) : lang;
}

function getLocalizedPath(path) {
  const root = i18n.rootUrl === '/' ? '' : (i18n.rootUrl || '');
  return `${root}${path}`.replace('//', '/');
}

async function fetchProductData(handle) {
  const language = getGraphQLLanguageCode();
  const query = `
    query getProduct($handle: String!, $language: LanguageCode) @inContext(language: $language) {
      product(handle: $handle) {
        id title handle
        images(first: 1) {
          edges {
            node {
              url
              altText
              width
              height
            }
          }
        }
        priceRange {
          minVariantPrice { amount currencyCode }
        }
        options { name values }
      }
    }
  `;

  try {
    const res = await fetch(STOREFRONT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query, variables: { handle, language } }),
    });
    const { data } = await res.json();
    const product = data?.product;
    if (!product) return null;
    product.featuredImage = product.images?.edges?.[0]?.node || null;
    return product;
  } catch (e) {
    return null;
  }
}

async function findVariantByOptions(handle, selectedOptions) {
  const language = getGraphQLLanguageCode();
  const query = `
    query getVariant($handle: String!, $selectedOptions: [SelectedOptionInput!]!, $language: LanguageCode) @inContext(language: $language) {
      product(handle: $handle) {
        variantBySelectedOptions(selectedOptions: $selectedOptions) {
          id availableForSale
          price { amount currencyCode }
        }
      }
    }
  `;

  try {
    const res = await fetch(STOREFRONT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query, variables: { handle, selectedOptions, language } }),
    });
    const { data } = await res.json();
    return data?.product?.variantBySelectedOptions || null;
  } catch (e) {
    return null;
  }
}

function formatPrice(amount, currencyCode) {
  const lang = document.documentElement.lang || 'en-US';
  return new Intl.NumberFormat(lang, { style: 'currency', currency: currencyCode }).format(amount);
}

function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  const drawerUrl = getLocalizedPath('/?sections=cart-drawer');
  
  fetch(drawerUrl)
    .then(r => r.json())
    .then(data => {
      if (data['cart-drawer']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data['cart-drawer'], 'text/html');
        const newDrawer = doc.querySelector('cart-drawer');
        if (newDrawer) drawer.innerHTML = newDrawer.innerHTML;
      }
      if (typeof drawer.cartRefreshHandler === 'function') drawer.cartRefreshHandler();
      if (typeof drawer.handleDrawerOpenViaEvent === 'function') {
        drawer.handleDrawerOpenViaEvent(new CustomEvent('cart:open', { detail: {} }));
      } else {
        drawer.open?.();
        drawer.setAttribute('open', '');
        document.dispatchEvent(new CustomEvent('cart:open', { detail: {} }));
      }
    });
}

function removeCardWithAnimation(card, productId) {
  card.style.transition = 'opacity 0.35s ease, transform 0.35s ease';
  card.style.opacity = '0';
  card.style.transform = 'scale(0.95)';
  setTimeout(() => {
    window.lionessWishlist.remove(productId);
    card.remove();
    checkEmpty();
  }, 350);
}

function equalizeOptionZones() {
  const grid = document.getElementById('wishlist-grid');
  if (!grid) return;

  const zones = Array.from(grid.querySelectorAll('.wishlist-card__options, .wishlist-card__options-placeholder'));
  zones.forEach(el => { el.style.minHeight = ''; });

  requestAnimationFrame(() => {
    const rows = new Map();
    zones.forEach(el => {
      const card = el.closest('.wishlist-card');
      const top = card.getBoundingClientRect().top;
      const key = Math.round(top);
      if (!rows.has(key)) rows.set(key, []);
      rows.get(key).push(el);
    });

    rows.forEach(rowZones => {
      let maxH = 0;
      rowZones.forEach(el => { maxH = Math.max(maxH, el.offsetHeight); });
      rowZones.forEach(el => { el.style.minHeight = maxH + 'px'; });
    });
  });
}

function renderCard(product, wishlistItem) {
  const card = document.createElement('div');
  card.className = 'wishlist-card';
  card.dataset.productHandle = product.handle;
  card.dataset.productId = wishlistItem.id;

  const imgUrl = product.featuredImage
    ? product.featuredImage.url + (product.featuredImage.url.includes('?') ? '&' : '?') + 'width=600'
    : null;

  const img = imgUrl
    ? `<img 
        src="${imgUrl}" 
        alt="${product.featuredImage.altText || product.title}" 
        width="${product.featuredImage.width || 600}"
        height="${product.featuredImage.height || 800}"
        loading="lazy">`
    : `<div class="wishlist-card__no-image"></div>`;

  const price = formatPrice(
    product.priceRange.minVariantPrice.amount,
    product.priceRange.minVariantPrice.currencyCode
  );

  const realOptions = product.options.filter(
    o => !(product.options.length === 1 && o.name === 'Title')
  );
  const hasOptions = realOptions.length > 0;

  const optionsHtml = hasOptions ? realOptions.map(option => `
    <div class="wishlist-card__option">
      <label>${option.name}</label>
      <div class="wishlist-card__select-wrap">
        <select data-option-name="${option.name}">
          <option value="">${option.name}</option>
          ${option.values.map(v => `<option value="${v.replace(/"/g, '&quot;')}">${v}</option>`).join('')}
        </select>
        <svg class="select-arrow" xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6">
          <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.2" fill="none"/>
        </svg>
      </div>
    </div>
  `).join('') : '';

  const productUrl = getLocalizedPath(`/products/${product.handle}`);

  card.innerHTML = `
    <div class="wishlist-card__image-wrap">
      <a href="${productUrl}" class="wishlist-card__image">${img}</a>
      <button class="wishlist-card__remove" data-product-id="${wishlistItem.id}" aria-label="Remove">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.3"/>
        </svg>
      </button>
    </div>
    <div class="wishlist-card__info">
      <a href="${productUrl}" class="wishlist-card__title">${product.title}</a>
      <div class="wishlist-card__price">${price}</div>
      ${hasOptions
        ? `<div class="wishlist-card__options">${optionsHtml}</div>`
        : `<div class="wishlist-card__options-placeholder"></div>`
      }
      <button class="wishlist-card__add-btn" ${hasOptions ? 'disabled' : ''}>
        ${hasOptions ? i18n.selectOptions : i18n.addToCart}
      </button>
    </div>
  `;

  const selects = card.querySelectorAll('select');
  const addBtn = card.querySelector('.wishlist-card__add-btn');

  async function onOptionChange() {
    const selectedOptions = Array.from(selects)
      .map(s => ({ name: s.dataset.optionName, value: s.value }))
      .filter(o => o.value);

    if (selectedOptions.length !== selects.length) {
      addBtn.disabled = true;
      addBtn.textContent = i18n.selectOptions;
      return;
    }

    addBtn.disabled = true;
    addBtn.textContent = '...';

    const variant = await findVariantByOptions(product.handle, selectedOptions);

    if (!variant || !variant.availableForSale) {
      addBtn.disabled = true;
      addBtn.textContent = variant ? i18n.soldOut : i18n.soldOut;
      return;
    }

    addBtn.disabled = false;
    addBtn.textContent = i18n.addToCart;
    addBtn.dataset.variantId = variant.id.split('/').pop();
  }

  selects.forEach(s => s.addEventListener('change', () => {
    card.classList.remove('wishlist-card--needs-options');
    onOptionChange();
  }));

  addBtn.addEventListener('click', async () => {
    const variantId = addBtn.dataset.variantId;
    if (!variantId) return;

    addBtn.disabled = true;
    addBtn.textContent = '...';

    try {
      await fetch(i18n.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      addBtn.textContent = '✓';
      setTimeout(() => removeCardWithAnimation(card, wishlistItem.id), 600);
      setTimeout(() => openCartDrawer(), 400);
    } catch (e) {
      addBtn.disabled = false;
      addBtn.textContent = i18n.addToCart;
    }
  });

  card.querySelector('.wishlist-card__remove').addEventListener('click', () => {
    removeCardWithAnimation(card, wishlistItem.id);
  });

  return card;
}

window.clearWishlist = function() {
  if (!confirm(i18n.clearConfirm)) return;

  const grid = document.getElementById('wishlist-grid');
  if (!grid) return;

  const cards = Array.from(grid.querySelectorAll('.wishlist-card'));
  if (!cards.length) return;

  window.lionessWishlist.clear();

  cards.forEach((card, i) => {
    setTimeout(() => {
      card.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      card.style.opacity = '0';
      card.style.transform = 'scale(0.95)';
      setTimeout(() => {
        card.remove();
        if (i === cards.length - 1) {
          checkEmpty();
          const tb = document.getElementById('wishlist-toolbar');
          if (tb) tb.style.display = 'none';
        }
      }, 300);
    }, i * 40);
  });
};

window.addAllToCart = async function() {
  const grid = document.getElementById('wishlist-grid');
  if (!grid) return;

  const cards = Array.from(grid.querySelectorAll('.wishlist-card'));
  if (!cards.length) return;

  const btn = document.getElementById('btn-add-all');
  const originalText = btn ? btn.textContent : '';
  if (btn) { btn.disabled = true; btn.textContent = '...'; }

  let addedCount = 0;
  let skippedCount = 0;
  const toRemove = [];

  for (const card of cards) {
    const addBtn = card.querySelector('.wishlist-card__add-btn');
    let variantId = addBtn?.dataset?.variantId;

    const hasOptions = card.querySelectorAll('.wishlist-card__options select').length > 0;
    if (hasOptions && !variantId) {
      card.classList.add('wishlist-card--needs-options');
      skippedCount++;
      continue;
    }

    if (!variantId) {
      const handle = card.dataset.productHandle;
      if (!handle) { skippedCount++; continue; }

      try {
        const res = await fetch(STOREFRONT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
          },
          body: JSON.stringify({
            query: `query($handle:String!){product(handle:$handle){variants(first:1){edges{node{id availableForSale}}}}}`,
            variables: { handle }
          }),
        });
        const { data } = await res.json();
        const node = data?.product?.variants?.edges?.[0]?.node;
        if (!node || !node.availableForSale) { skippedCount++; continue; }
        variantId = node.id.split('/').pop();
      } catch {
        skippedCount++;
        continue;
      }
    }

    try {
      await fetch(i18n.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      addedCount++;
      toRemove.push({ card, productId: card.dataset.productId });
    } catch {
      skippedCount++;
    }
  }

  toRemove.forEach(({ card, productId }, i) => {
    setTimeout(() => removeCardWithAnimation(card, productId), i * 80);
  });

  if (addedCount > 0) {
    setTimeout(() => openCartDrawer(), 300);
  }

  if (btn) {
    btn.disabled = false;
    btn.textContent = originalText;
  }
};

window.shareWishlist = function() {
  const items = window.lionessWishlist?.getAll() || [];
  if (!items.length) return;

  const handles = items.map(i => i.handle).join(',');
  const wishlistPath = getLocalizedPath('/pages/wishlist');
  const url = `${window.location.origin}${wishlistPath}?handles=${encodeURIComponent(handles)}`;

  let modal = document.getElementById('wishlist-share-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'wishlist-share-modal';
    modal.innerHTML = `
      <div class="wsm__overlay"></div>
      <div class="wsm__box">
        <button class="wsm__close" aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 12 12" fill="none">
            <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.3"/>
          </svg>
        </button>
        <p class="wsm__title">${i18n.shareTitle}</p>
        <p class="wsm__hint">${i18n.shareHint}</p>
        <div class="wsm__row">
          <input class="wsm__input" type="text" readonly />
          <button class="wsm__copy">${i18n.copy}</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    modal.querySelector('.wsm__overlay').addEventListener('click', closeShareModal);
    modal.querySelector('.wsm__close').addEventListener('click', closeShareModal);
    modal.querySelector('.wsm__copy').addEventListener('click', () => {
      const input = modal.querySelector('.wsm__input');
      navigator.clipboard.writeText(input.value).then(() => {
        const btn = modal.querySelector('.wsm__copy');
        btn.textContent = i18n.copied;
        setTimeout(() => {
          closeShareModal();
          btn.textContent = i18n.copy;
        }, 1500);
      });
    });
  }

  modal.querySelector('.wsm__input').value = url;
  modal.classList.add('wsm--open');
};

function closeShareModal() {
  document.getElementById('wishlist-share-modal')?.classList.remove('wsm--open');
}

function checkEmpty() {
  const grid = document.getElementById('wishlist-grid');
  const empty = document.getElementById('wishlist-empty');
  const toolbar = document.getElementById('wishlist-toolbar');
  if (!grid || !empty) return;

  const isEmpty = grid.querySelectorAll('.wishlist-card').length === 0;
  empty.style.display = isEmpty ? 'block' : 'none';
  if (toolbar) toolbar.style.display = isEmpty ? 'none' : 'flex';
}

async function initWishlistPage() {
  const grid = document.getElementById('wishlist-grid');
  const loading = document.getElementById('wishlist-loading');
  const empty = document.getElementById('wishlist-empty');
  const toolbar = document.getElementById('wishlist-toolbar');
  if (!grid) return;

  if (loading) loading.style.display = 'none';

  const urlParams = new URLSearchParams(window.location.search);
  const sharedHandles = urlParams.get('handles');
  const isReadOnly = !!sharedHandles;

  if (isReadOnly) {
    if (toolbar) toolbar.style.display = 'none';
    const pageTitle = document.querySelector('.page-title, h1');
    if (pageTitle) pageTitle.textContent = i18n.shareTitle;

    const handles = sharedHandles.split(',').map(h => h.trim()).filter(Boolean);
    if (!handles.length) {
      if (empty) empty.style.display = 'block';
      return;
    }

    const results = await Promise.all(
      handles.map(handle => fetchProductData(handle).then(product => ({
        product,
        item: { id: handle, handle, variantId: null }
      })))
    );

    results.forEach(({ product, item }) => {
      if (!product) return;
      const card = renderCard(product, item);
      card.querySelector('.wishlist-card__remove')?.remove();
      grid.appendChild(card);
    });

    checkEmpty();
    if (toolbar) toolbar.style.display = 'none';
    setTimeout(equalizeOptionZones, 100);
  } else {
    const items = window.lionessWishlist?.getAll() || [];

    if (items.length === 0) {
      if (empty) empty.style.display = 'block';
      if (toolbar) toolbar.style.display = 'none';
      return;
    }

    const results = await Promise.all(
      items.map(item => fetchProductData(item.handle).then(product => ({ product, item })))
    );

    results.forEach(({ product, item }) => {
      if (!product) return;
      grid.appendChild(renderCard(product, item));
    });

    checkEmpty();
    setTimeout(equalizeOptionZones, 100);
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(equalizeOptionZones, 150);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlistPage);
} else {
  initWishlistPage();
}